import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Badges from '../Badges/Badges';
import H from '../H/H';
import Icon from '../Icon/Icon';
import Menu from '../Menu/Menu';

/**
 * `Cover` is a header container that displays a cover art and information that summarizes the item that the header
 * represents. The cover art can be an image or a custom component, such as a video player.
 *
 * If *fields* contain a property named `published` it will be visualized as an icon and the text "Published" or
 * "Unpublished". These texts can be set through props.
 *
 * @property {node} badges One or more badges that will be added to the top left corner of the cover image, to the right of
 * *tag* if that prop is defined. Defaults to no badges.
 * @property {array} badgeNames An array of icon names supported by the {@link Icon} component. Defaults to no badges.
 * @property {node} children Custom child components that will be placed after the fields.
 * @property {string} className The class name of this component. Will be appended to "core-cover".
 * @property {string|node} cover The URI of the cover image you want to show in the sidebar, or a custom component that
 * should act as a cover (e.g. a video player).
 * @property {array} fields An array of objects of type `{ name, value }` properties that will be displayed in the sidebar.
 * The fields will be read-only.
 * @property {string|number} id A field directly under the title that shows the ID of the item shown in the lightbox. The
 * field is not shown by default.
 * @property {string} ingress An ingress text that will be shown under the ID field. It can be used to show additional asset
 * or category IDs. Defaults to no text.
 * @property {number} imageHeight Setting this adds a "?height={*imageHeight*}" parameter to the *cover* prop if it is a URL
 * and does not already have query parameters. Can be set to "0" or less to omit the parameter. Defaults to "300" for
 * backward compatibility reasons.
 * @property {number} level Determines the heading level of the title component. Defaults to 2 (`h2`).
 * @property {function} onCreateMenuItems If this callback is set the header component will add a menu button to the right of
 * the title. Clicking on this button will call the *onCreateMenuItems* callback and display the menu items it returns.
 * @property {string} publishedText The published state label. Defaults to "Published".
 * @property {string} tag A tag that will be displayed over the cover image (or custom component). Defaults to no tag.
 * @property {string} title The sidebar title. A `h2` will be used by default, but you may change the heading level by
 * setting the `level` prop. Title defaults to "Untitled".
 * @property {string} unpublishedText The unpublished state label. Defaults to "Unpublished".
 * @property {boolean} noCoverArt If set to `true`, no elements for the cover art will be generated.
 *
 * @see Badges, Icon
 */
export default class Cover extends Component {

  static displayName = 'Cover';

  static propTypes = {
    badges: PropTypes.node,
    badgeNames: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.node,
    className: PropTypes.string,
    cover: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    fields: PropTypes.arrayOf(PropTypes.object),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ingress: PropTypes.string,
    imageHeight: PropTypes.number,
    level: PropTypes.number,
    noCoverArt: PropTypes.bool,
    onCreateMenuItems: PropTypes.func,
    publishedText: PropTypes.string,
    tag: PropTypes.string,
    title: PropTypes.string,
    unpublishedText: PropTypes.string,
  };

  static defaultProps = {
    fields: [],
    imageHeight: 300,
    level: 2,
    publishedText: 'Published',
    unpublishedText: 'Unpublished',
  };

  static createFields(attrs = [], publishedText, unpublishedText) {
    const items = [];

    attrs.forEach((a, index) => {
      const valKey = `${index}-1`;
      const fieldCls = `field-${(a.name || '').replace(/ /g, '-').toLowerCase()}`;
      let icon;
      let name = a.name;
      let value = a.value;

      if (a.name === 'published') {
        const type = a.value ? 'ok' : 'warning';

        name = a.value ? publishedText : unpublishedText;
        icon = <Icon name={type} className={type} />;
      } else if (typeof a.value === 'boolean') {
        value = <Icon key={valKey} name={a.value ? 'checkbox-checked' : 'checkbox-unchecked'} className="boolean" />;
      }
      items.push(
        <tr key={index}>
          <th colSpan={icon && '2'}><label htmlFor="sidebaritem" key={index} className={fieldCls}>{icon}{name}</label></th>
          <td>{value}</td>
        </tr>
      );
    });
    return items;
  }

  render() {
    const { badges, badgeNames, children, className, id, imageHeight, level, onCreateMenuItems, publishedText, tag, title, unpublishedText, noCoverArt, ingress } = this.props;
    let { cover, fields } = this.props;

    if (typeof cover === 'string') {
      const hasQueryParameters = cover.lastIndexOf('?') > -1;

      cover = <div role="img" aria-label="cover image" className="cover" style={{ backgroundImage: `url(${cover}${!hasQueryParameters && imageHeight > 0 ? `?height=${imageHeight}` : ''})` }} />;
    }
    if (fields.length) {
      fields = (
        <table className="fields" cellSpacing={0}>
          <tbody>
            {Cover.createFields(fields, publishedText, unpublishedText)}
          </tbody>
        </table>
      );
    }
    const coverArt = !noCoverArt && (
      <div className="cover-art">
        {cover}
        {(tag || badges || badgeNames) && <Badges label={tag} iconNames={badgeNames}>{badges}</Badges>}
      </div>
    );
    return (
      <header className={cx('core-cover', className)}>
        {coverArt}
        <div className="info">
          <H level={level}>
            <span>{(title && title.length && title) || 'Untitled'}</span>
            {onCreateMenuItems && <Menu onCreateMenuItems={onCreateMenuItems}/>}
          </H>
          {id && <span className="item-id">{id}</span>}
          {ingress && <span className="ingress">{ingress}</span>}
          {fields}
        </div>
        {children}
      </header>
    );
  }

}
